import { mwBucket } from '../game/scoring';
import { FIELD_URL } from './endpoints';
import { storage } from './storage';

/**
 * The field readout's half of the wire (GDD §4.7).
 *
 * Everything here fails open. A submission that does not land is a counter
 * that does not move, and a read that does not land is a readout that does not
 * appear — neither is anything the player has to be told about, and neither
 * touches play.
 */

const SUBMITTED_KEY = 'wirespan:field';
/** Enough grids to cover any plausible run of days on one device. */
const SUBMITTED_CAP = 60;
/** Longer than this and the answer is not worth having. */
const TIMEOUT_MS = 3000;

/** The counters for one grid, exactly as stored. Nothing here is derived. */
export interface FieldData {
  grid: number;
  n: number;
  hist: number[];
  tiers: Record<string, number>;
  mwSum: number;
  mwN: number;
  coldSum: number;
  coldN: number;
  perfect: number;
  flawless: number;
  beatRef: number;
}

export interface FieldResult {
  grid: number;
  mw: number;
  cold: number | null;
  /** The rank as a word: superconductor, master, journeyman, apprentice. */
  tier: string;
  perfect: boolean;
  flawless: boolean;
  beatRef: boolean;
}

/**
 * One result as the counters received it.
 *
 * Kept because a result that improves has to be moved rather than re-counted,
 * and moving it means naming the entry to take back out.
 */
export interface FieldSubmission {
  bucket: number;
  mw: number;
  cold: number | null;
  tier: string;
  perfect: boolean;
  flawless: boolean;
  beatRef: boolean;
  /**
   * The version of the scoring unit the reading is in. The server requires
   * it, so a reading in another unit — or an unmarked one from an older
   * build — is excluded rather than averaged into a room reading it cannot
   * be compared with (GDD §12).
   */
  sv?: number;
}

/**
 * What this browser has sent for each grid.
 *
 * A grid is absent when nothing has been sent for it, and maps to `null` when
 * it was counted by a build that kept only the grid number — counted, but with
 * no record of what, so there is nothing to take back out.
 *
 * Held in memory as well as in storage because the submission happens as the
 * page is going away, where there is no time to await a read.
 */
let sent = new Map<number, FieldSubmission | null>();
let ready = false;

const FLAGS = ['perfect', 'flawless', 'beatRef'] as const;

/** A stored entry, or `null` if it is not one this build can use. */
function asSubmission(value: unknown): FieldSubmission | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.bucket !== 'number' || typeof v.mw !== 'number' || typeof v.tier !== 'string') {
    return null;
  }
  return {
    bucket: v.bucket,
    mw: v.mw,
    cold: typeof v.cold === 'number' ? v.cold : null,
    tier: v.tier,
    perfect: v.perfect === true,
    flawless: v.flawless === true,
    beatRef: v.beatRef === true,
    ...(typeof v.sv === 'number' ? { sv: v.sv } : {}),
  };
}

export async function loadFieldState(): Promise<void> {
  sent = new Map();
  try {
    const raw = await storage.get(SUBMITTED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    // The first version of this store kept grid numbers and nothing else.
    // Those grids are counted and stay counted; they simply cannot be amended.
    if (Array.isArray(parsed)) {
      for (const grid of parsed) if (typeof grid === 'number') sent.set(grid, null);
    } else if (parsed && typeof parsed === 'object') {
      const entries = (parsed as { sent?: Record<string, unknown> }).sent ?? {};
      for (const [key, value] of Object.entries(entries)) {
        const grid = Number(key);
        if (Number.isInteger(grid)) sent.set(grid, asSubmission(value));
      }
    }
  } catch {
    sent = new Map();
  }
  ready = true;
}

/** Has this browser already been counted on `grid`? */
export function alreadyCounted(grid: number): boolean {
  return sent.has(grid);
}

/** What the counters hold for this browser on `grid`, where that is known. */
export function sentFor(grid: number): FieldSubmission | null {
  return sent.get(grid) ?? null;
}

function remember(grid: number, entry: FieldSubmission): void {
  sent.set(grid, entry);
  const kept = [...sent.keys()].sort((a, b) => b - a).slice(0, SUBMITTED_CAP);
  sent = new Map(kept.map((g) => [g, sent.get(g) ?? null]));
  void storage.set(SUBMITTED_KEY, JSON.stringify({ v: 2, sent: Object.fromEntries(sent) }));
}

/** Fire and forget: this runs as the page is unloading, so nothing awaits it. */
function post(path: string, body: unknown): void {
  try {
    void fetch(`${FIELD_URL}/${path}`, {
      method: 'POST',
      body: JSON.stringify(body),
      keepalive: true,
      headers: { 'content-type': 'application/json' },
    }).catch(() => undefined);
  } catch {
    /* the counter simply does not move */
  }
}

function entryOf(result: FieldResult): FieldSubmission {
  return {
    bucket: mwBucket(result.mw),
    mw: result.mw,
    cold: result.cold,
    tier: result.tier,
    perfect: result.perfect,
    flawless: result.flawless,
    beatRef: result.beatRef,
    sv: 2,
  };
}

const same = (a: FieldSubmission, b: FieldSubmission): boolean =>
  a.bucket === b.bucket &&
  a.mw === b.mw &&
  a.tier === b.tier &&
  FLAGS.every((flag) => a[flag] === b[flag]);

/**
 * Put this result into the day's counters, or move the one already there.
 *
 * Marked as sent before the request rather than after: this runs as the page
 * is unloading, so there is no answer to wait for, and counting a player twice
 * would overstate the room. A lost write is a player missing from a total,
 * which is the honest failure of the two.
 *
 * A player who improves after their first submission has landed is amended
 * rather than counted again — the entry moves up the scale and `n` does not
 * change. Without it, a grid left at journeyman and returned to for a
 * superconductor would tell the room the journeyman, forever.
 */
export function submitResult(result: FieldResult): void {
  if (!ready) return;
  const entry = entryOf(result);
  const prev = sent.get(result.grid);

  if (prev === undefined) {
    remember(result.grid, entry);
    post(`${result.grid}/result`, entry);
    return;
  }
  // Counted, with no record of what by. There is nothing to take back out, so
  // what the counters hold stands.
  if (prev === null) return;
  // Nothing moved, or the reading came back down — a restored session can hand
  // back a best that is not better, and an amend only ever climbs.
  if (same(prev, entry) || entry.mw < prev.mw) return;

  remember(result.grid, entry);
  post(`${result.grid}/amend`, { from: prev, to: entry });
}

/** The day's counters, or `null` if they cannot be had. */
export async function fetchField(grid: number): Promise<FieldData | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${FIELD_URL}/${grid}`, {
      signal: controller.signal,
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<FieldData>;
    if (typeof data?.n !== 'number' || !Array.isArray(data.hist)) return null;
    return {
      grid,
      n: data.n,
      hist: data.hist,
      tiers: data.tiers ?? {},
      mwSum: data.mwSum ?? 0,
      mwN: data.mwN ?? 0,
      coldSum: data.coldSum ?? 0,
      coldN: data.coldN ?? 0,
      perfect: data.perfect ?? 0,
      flawless: data.flawless ?? 0,
      beatRef: data.beatRef ?? 0,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
